import { ReactNode, useState } from "react";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { Collapse } from "reactstrap";

interface Props<T> {
  loading: boolean;
  items: T[];
  title: string | undefined;
  getItemTitle: (item: T) => ReactNode;
  getItemSubtitle: ((item: T) => ReactNode) | undefined;
  getItemIcon?: (item: T) => ReactNode;
  getItemStyle?: (item: T) => ReactNode;
  onClick?: (item: T) => void;
  CustomRenderer?: (item: T) => ReactNode;
  titleCSS?: string;
  containerCSS?: string;
  emptyMessage?: string;
  collapsible?: boolean;
}

export function BasicTableView<T>({
  loading,
  items,
  title,
  getItemTitle,
  getItemSubtitle,
  getItemIcon,
  getItemStyle,
  onClick,
  CustomRenderer,
  titleCSS,
  containerCSS,
  emptyMessage,
  collapsible = false,
}: Props<T>) {
  const [open, setOpen] = useState(true);

  const Title = title ? (
    <div
      className={`bg-superlight px-3 py-2 font-weight-600 text-dark mb-1 ${titleCSS} ${
        collapsible ? "cursor-pointer" : ""
      }`}
      onClick={collapsible ? () => setOpen((o) => !o) : undefined}
    >
      {collapsible && (
        <i className={`mdi mdi-chevron-${open ? "down" : "right"} mr-2`} />
      )}
      {title}
    </div>
  ) : null;

  const Table = (
    <div className={containerCSS}>
      {loading ? (
        <>
          <SkeletonLoader height={55} css="my-1" />
          <SkeletonLoader height={55} css="my-1" />
          <SkeletonLoader height={55} css="my-1" />
          <SkeletonLoader height={55} css="my-1" />
        </>
      ) : Array.isArray(items) && items.length > 0 ? (
        items?.map((item, i) => {
          const itemTitle = getItemTitle(item);

          return (
            <div
              key={i}
              className={`d-flex align-items-center justify-content-between rounded-sm btn-oauth cursor-pointer w-100 ${
                getItemStyle ? getItemStyle(item) : ""
              } ${onClick ? "bg-ultralight--hover" : ""}`}
              style={{ padding: "10px 17px", margin: "2px 0" }}
              onClick={onClick ? () => onClick(item) : undefined}
            >
              {getItemIcon && getItemIcon(item)}
              <div className="text-truncate" style={{ flex: 1 }}>
                <h4
                  className="mb-0 text-truncate"
                  title={typeof itemTitle === "string" ? itemTitle : undefined}
                >
                  {itemTitle}
                </h4>
                {getItemSubtitle && <small>{getItemSubtitle(item)}</small>}
              </div>
              {CustomRenderer
                ? CustomRenderer(item)
                : onClick && (
                    <i
                      className="mdi mdi-chevron-right"
                      style={{ fontSize: 26 }}
                    />
                  )}
            </div>
          );
        })
      ) : (
        <div className="px-3 py-4 font-weight-500 bg-ultralight rounded">
          {emptyMessage || "No items have been added."}
        </div>
      )}
    </div>
  );

  if (collapsible) {
    return (
      <>
        {Title}
        <Collapse isOpen={open}>{Table}</Collapse>
      </>
    );
  }

  return (
    <>
      {title && Title}
      {Table}
    </>
  );
}

export default BasicTableView;
